<template lang="pug">
.chat(:class="chatClass")
  .header(:class="headerAdditionalClass")
    slot(name="header")
  ul.messages(v-if="!isChatInitialized")
    template(v-for="i in 3")
      li.self
        q-skeleton(:width="`${100 - i * 20}px`")
      li.other
        q-skeleton(:width="`${150 - i * 20}px`")
  ul.messages(v-else, ref="messagesContainer")
    li.other.invisible(v-if="mayObserve", v-observe-visibility="visibilityChanged")
    li.welcome(v-if="showWelcome")
      .welcome-container
        .welcome-wrapper
          .welcome-img
            img(:src="require('../../../assets/images/avatar.png')")
          .welcome-text-container
            b {{ agentLiteLocales.welcome_main }}
            div {{ agentLiteLocales.welcome_secondary }}
    ul.chat-messages(:class="chatMessagesClass")
      template(v-for="msg in messages") 
        li(:key="msg.id", :class="getMsgClass(msg)")
          pre(:title="msg.id") {{ msg.content }}
        li.rate(:key="`${msg.id}_rate`", v-if="msg.role === 'agent'")
          inline-svg.thumb-up(
            :src="require('../../../assets/icons/ai/like.svg')",
            :class="getMsgRatingClass(msg, true)",
            @click="changeRating(msg, true)"
          )
          inline-svg.thumb-down(
            :src="require('../../../assets/icons/ai/like.svg')",
            :class="getMsgRatingClass(msg, false)",
            @click="changeRating(msg, false)"
          )
    li.other.sending(v-if="isPolling") 
      .loader

  .footer(:class="footerAdditionalClass")
    .text-box(
      ref="textInput",
      :contenteditable="contentEditable",
      :data-placeholder="footerPlaceholder",
      @input="onTextInput",
      @keydown.enter="handleEnterPress"
    )
    button#sendMessage(@click="sendNewMessage") 
      inline-svg(:src="require('../../../assets/icons/ai/send.svg')", :class="sendButtonIconClass")
</template>

<script setup>
import { ref, onMounted, nextTick, computed, onBeforeUnmount } from "vue";
import { agentLiteLocales } from "@/services/useLocales";
import { handleError } from "../../../services/handleErrors";
import { uniqueObjects } from "../../../services/uniqueObjects";
import { Notify } from "quasar";
import backend from "@/api";

const props = defineProps({
  /* показатель - может ли пользователь использовать чат */
  permitted: { type: Boolean, default: false },
  /* набор путей для запросов в виде объекта { main: 'api/v3/ai/chats/', name: 'lite' } */
  paths: { type: Object, default: () => ({ main: "", name: "" }) },
  /* главный ли агент */
  isLite: { type: Boolean, default: false },
  /* отображение welcome при отсутствии сообщений в чате */
  hideWelcome: { type: Boolean, default: false },
  /* дополнительный класс для хедера, т.к. в дашборде он должен выглядеть иначе */
  headerAdditionalClass: { type: String, default: "" },
  /* дополнительный класс для футера, т.к. в дашборде он должен выглядеть иначе */
  footerAdditionalClass: { type: String, default: "" },
  /* флаг отображения плейсхолдера в футере, т.к. в дашборде плейсхолдера нет */
  hideFooterPlaceholder: { type: Boolean, default: false },
  /* флаг надобности инициализировать чат, чат для дашборда инициализируется в родительском компоненте */
  focusFooterOnLoaded: { type: Boolean, default: true },
  /* id чата. Присылается родительским компонентом при создании чата или получении списка чатов дашборда */
  chatId: { type: Number || null, default: null },
  /* флаг что чат в целом создан из дашборда для назначения класса на весь компонент */
  fromDashboard: { type: Boolean, default: false },
});

const emit = defineEmits([
  /** 
   * Эмит вызывается из родительского компонента chartGenerator
   * и нужен для создания графика в зависимости от параметров присланных агентом.
   * Пример параметров ожидаемых эмитов: {
      "id": 406,
      "chat_id": 78,
      "user_id": 1,
      "role": "agent",
      "content": "Запрос возвращает количество просроченных заявок, сгруппированных по каждому технику. Просроченные заявки определяются условием, что дата контрольного срока ('finish_date_plane') меньше текущей даты и статус заявки не является архивным.",
      "rating": null,
      "options": {
          "axis": {
              "x": "Техники",
              "y": "Количество просроченных заявок"
          },
          "type": "bar",
          "filters": {
              "finish_date_plane": "lesser today",
              "status.terminated": "equal false"
          },
          "grouping": {
              "executor.id": "by_all"
          },
          "operation": {
              "status.id": "count"
          },
          "chart_name": "Просроченные заявки по техникам"
      }
    }
   */
  "create-chart",
]);

const chatId = ref(props.chatId || "");
const chatVisible = ref(false);

const messages = ref([]);
const lastMessageId = ref();
const pollingTimeout = ref();

// 10 min = 60 * 10 * 1000
const maxPollingTime = 600000;
const currentPollingTime = ref(0);

const messagesContainer = ref(null);
const textInput = ref(null);

const page = ref(1);
const hasMore = ref(true);

const isPolling = ref(false);
const isLoading = ref(false);
const isSendDisabled = ref(true);
const mayObserve = ref(false);
const isChatInitialized = ref(false);

const contentEditable = computed(() => (isLoading.value ? false : "plaintext-only"));
const chatClass = computed(() => (props.fromDashboard ? "dashboard" : ""));
const chatMessagesClass = computed(() => ({ "chat-messages--with-floating": props.isLite }));
const sendButtonIconClass = computed(() => ({
  "send-active": !isSendDisabled.value,
  "send-disabled": isSendDisabled.value,
}));
const showWelcome = computed(() => !messages?.length && !props.hideWelcome);
const footerPlaceholder = computed(() => (props.hideFooterPlaceholder ? "" : agentLiteLocales.value.chat_placeholder));

const getMsgClass = msg => ({ self: msg.role === "user", other: msg.role === "agent" });
const getMsgRatingClass = (msg, bool) => ({ filled: msg.rating === bool });

const changeRating = async (msg, rating) => {
  try {
    const index = messages.value.findIndex(el => el.id === msg.id);
    if (index !== -1) messages.value[index].rating = rating;

    const response = await backend.patch(
      `${props.paths.main}/${props.paths.name}/${chatId.value}/messages/${msg.id}/rate`,
      {
        message: { rating },
      },
    );
  } catch (e) {
    await handleError(e);
  }
};

const onTextInput = e => {
  if (e.target.innerHTML === "" || e.target.innerHTML === "<br>") isSendDisabled.value = true;
  else isSendDisabled.value = false;
};

const onTextInputBlur = () => {
  if (textInput.value.innerHTML === "<br>") textInput.value.innerHTML = "";

  textInput.value.dataset.placeholder = footerPlaceholder.value;

  isSendDisabled.value = true;
};

const clearTextInput = () => {
  if (textInput.value) {
    textInput.value.blur();
    textInput.value.innerHTML = "";
    textInput.value.dataset.placeholder = footerPlaceholder.value;
    isSendDisabled.value = true;
  }
};

const handleEnterPress = e => {
  if (!(e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    sendNewMessage();
  } else {
    addNewLine();
  }
};

const setCarret = () => {
  const range = document.createRange();
  const selection = window.getSelection();

  range.setStart(
    textInput.value.childNodes[textInput.value.childNodes.length - 1],
    textInput.value.childNodes[textInput.value.childNodes.length - 1].length,
  );
  range.collapse(true);

  selection.removeAllRanges();
  selection.addRange(range);
};

const sendNewMessage = async e => {
  try {
    const newMessage = cleanMessage(textInput.value.innerHTML);

    if (!newMessage) return;

    textInput.value.innerHTML = "";
    textInput.value.dataset.placeholder = footerPlaceholder.value;
    isSendDisabled.value = true;

    const message = await backend.create(`${props.paths.main}/${props.paths.name}/${chatId.value}/messages`, null, {
      message: { content: newMessage },
    });

    messages.value.push(message.data);

    startPollig();

    nextTick(scrollToBottom);
  } catch (e) {
    await handleError(e);
  }
};

const addNewLine = e => {
  textInput.value.innerHTML += "<div><br></div>";
  setCarret();
};

const cleanMessage = html => {
  return html
    .replace(/<div>|<br.*?>/gi, "\n")
    .replace(/<\/div>/g, "")
    .trim()
    .replace(/\n/g, "\r\n")
    .replace(/&nbsp;/, " ");
};

const visibilityChanged = async isVisible => {
  if (!isLoading.value && hasMore.value && isVisible) {
    await loadHistory();
  }
};

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const pollRequest = async () => {
  if (currentPollingTime.value > maxPollingTime) abortPolling();

  try {
    lastMessageId.value = messages.value[messages.value.length - 1]?.id || null;

    const response = await backend.index(
      `${props.paths.main}/${props.paths.name}/poll?last_message_id=${lastMessageId.value}`,
    );

    if (response?.data?.length) {
      stopPolling();

      const data = response.data;
      messages.value = uniqueObjects([...data, ...messages?.value]);

      emit("create-chart", data[0]);
    }
  } catch (e) {
    // экспоненциально увеличиваем время ожидания до следующего запроса при возникновении ошибки (нет смысла нагружать систему)!
    console.log(e);

    if (e?.response?.status.toString().split("")[0] === "5") {
      abortPolling();
    }

    currentPollingTime.value += 5000;
    await new Promise(r => setTimeout(r, 5000));
  } finally {
    if (isPolling.value) await pollData();
  }
};

const pollData = async () => {
  if (!isPolling.value) return;

  currentPollingTime.value += 5000;
  pollingTimeout.value = setTimeout(pollRequest, 5000);
};

const startPollig = () => {
  if (isPolling.value) return;

  isPolling.value = true;

  pollData();
};

const stopPolling = () => {
  if (!isPolling.value) return;

  isPolling.value = false;

  currentPollingTime.value = 0;
  clearTimeout(pollingTimeout.value);
};

const abortPolling = async () => {
  stopPolling();
  Notify.create(agentLiteLocales.value.server_response_timeout);
};

const loadHistory = async () => {
  if (!hasMore.value || isLoading.value) return;

  isLoading.value = true;
  mayObserve.value = false;

  try {
    const container = messagesContainer.value;
    let restoreScroll = false;
    let oldScrollHeight = 0;
    let oldScrollTop = 0;

    if (container && container.scrollHeight > 0) {
      oldScrollHeight = container.scrollHeight;
      oldScrollTop = container.scrollTop;
      restoreScroll = true;
    }

    const response = await backend.index(
      `${props.paths.main}/${props.paths.name}/${chatId.value}/messages?json=${JSON.stringify({
        table: { page: page.value, sortBy: "id", descending: true, rowsPerPage: 20 },
      })}`,
      { infinite_scroll: { page: page.value } },
    );

    const data = response?.data?.data;

    if (data?.length === 0) {
      hasMore.value = false;
      return;
    }

    messages.value = uniqueObjects([...data, ...messages?.value]);

    await nextTick();

    page.value++;

    // Восстанавливаем позицию прокрутки
    if (restoreScroll && container) {
      const newScrollHeight = container.scrollHeight;
      container.scrollTop = oldScrollTop + (newScrollHeight - oldScrollHeight);
    }
  } catch (e) {
    await handleError(e);
  } finally {
    isLoading.value = false;
    mayObserve.value = true;
  }
};

const initializeChat = async () => {
  try {
    // chatId в случае с чатами дашборда приходит от родителя, в обратном случае chatId === "" и назначается в запросе ниже
    if (!chatId.value) {
      const response = await backend.index(`${props.paths.main}/${props.paths.name}/obtain`);

      chatId.value = response?.data?.id;
    }

    window.addEventListener("online", startPollig);
    window.addEventListener("offline", stopPolling);

    await loadHistory();

    const lastMessage = messages.value.at(-1);

    if (lastMessage?.role === "user") {
      startPollig();
    } else if (lastMessage?.role === "agent") {
      emit("create-chart", lastMessage);
    } else {
      emit("create-chart", "empty");
    }

    if (props.focusFooterOnLoaded) {
      textInput.value?.focus();
    }
    textInput.value?.addEventListener("blur", onTextInputBlur);
  } catch (e) {
    await handleError(e);
  } finally {
    isChatInitialized.value = true;
    scrollToBottom();
  }
};

onMounted(async () => {
  if (props.permitted) await initializeChat();
});

onBeforeUnmount(() => {
  clearTextInput();
  stopPolling();
});
</script>

<style lang="scss" scoped>
$scrollbar-width: 5px;
$chat-thread-bgd-color: white;
$chat-thread-bgd-color-dashboard: #9dafff;
$chat-thread-text-color: #0d1f3c;
$chat-thread-msg-arrow-size: 10px;
$chat-thread-avatar-size: 25px;
$chat-thread-offset: #{$chat-thread-avatar-size + 20px};
$default-shadow-color: rgb(0, 0, 0);

@mixin fancy-background() {
  background: -moz-linear-gradient(-45deg, #183850 0, #183850 25%, #192c46 50%, #22254c 75%, #22254c 100%);
  background: -webkit-linear-gradient(-45deg, #183850 0, #183850 25%, #192c46 50%, #22254c 75%, #22254c 100%);
  background-repeat: no-repeat;
  background-attachment: fixed;
}

@mixin fancy-scrollbar() {
  &::-webkit-scrollbar {
    width: $scrollbar-width;
  }
  &::-webkit-scrollbar-track {
    border-radius: $scrollbar-width;
    background-color: rgba(25, 147, 147, 0.1);
  }
  &::-webkit-scrollbar-thumb {
    border-radius: $scrollbar-width;
    background-color: $chat-thread-bgd-color;
  }
}

@mixin scrolling-shadows(
  $background-color: transparent,
  $shadow-intensity: 0.5,
  $shadow-color: $default-shadow-color,
  $cover-size: 40px,
  $shadow-size: 14px
) {
  background: linear-gradient($background-color 30%, rgba($background-color, 0)),
    linear-gradient(rgba($background-color, 0), $background-color 70%) 0 100%,
    radial-gradient(50% 0, farthest-side, rgba($shadow-color, $shadow-intensity), rgba($shadow-color, 0)),
    radial-gradient(50% 100%, farthest-side, rgba($shadow-color, $shadow-intensity), rgba($shadow-color, 0)) 0 100%;
  background: linear-gradient($background-color 30%, rgba($background-color, 0)),
    linear-gradient(rgba($background-color, 0), $background-color 70%) 0 100%,
    radial-gradient(farthest-side at 50% 0, rgba($shadow-color, $shadow-intensity), rgba($shadow-color, 0));
  background-repeat: no-repeat;
  background-color: $background-color;
  background-size: 100% $cover-size, 100% $cover-size, 100% $shadow-size, 100% $shadow-size;
  background-attachment: local, local, scroll, scroll;
}

.chat {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  border-radius: 0;
  transition: all 250ms ease-out;

  :focus {
    outline: 0;
  }

  button {
    background: transparent;
    border: 0;
    color: white;
    text-transform: uppercase;
    border-radius: 3px;
    cursor: pointer;
    padding: 5px;
  }

  .header {
    display: flex;
    gap: 12px;
    flex-shrink: 0;
    background: linear-gradient(white, #9bb5ff);
    margin: -16px -16px 0 -16px;
    border-top-right-radius: 32px;
    border-top-left-radius: 32px;
    background: linear-gradient(#ffffff66, #9bb5ff66);
    padding: 16px;

    .img {
      background-color: #dee4ff;
      background-image: url("../../../assets/images/avatar.png");
      background-size: contain;
      width: 40px;
      height: 40px;
      border-radius: 50%;
    }

    .text {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      color: #fff;
      font-weight: bold;
      font-size: 16px;
    }

    button {
      margin-left: auto;
      flex-shrink: 0;
    }

    &.dashboard-chat-header {
      margin: 0;
      background: #fff;
      font-weight: 600;
    }
  }

  .messages {
    padding: 16px 10px 0 10px;
    margin: 0;
    list-style: none;
    overflow-y: auto;
    overflow-x: hidden;
    flex-grow: 1;
    border-radius: 4px;
    background: transparent;
    @include fancy-scrollbar;

    .chat-messages--with-floating {
      margin-top: 85px;
    }

    li {
      position: relative;
      clear: both;
      display: inline-block;
      padding: 16px;
      margin: 0 0 16px 0;
      font: 14px/20px "Noto Sans", sans-serif;
      background-color: $chat-thread-bgd-color;
      word-wrap: break-word;
      max-width: 81%;

      pre {
        font-family: inherit;
        font-size: inherit;
        white-space: break-spaces;
        margin: 0;
      }

      &:before {
        position: absolute;
        top: 0;
        width: $chat-thread-avatar-size;
        height: $chat-thread-avatar-size;
        border-radius: $chat-thread-avatar-size;
        content: "";
        background-size: cover;
      }

      &:after {
        position: absolute;
        bottom: 0;
        content: "";
        width: 0;
        height: 0;
        border-bottom: $chat-thread-msg-arrow-size solid $chat-thread-bgd-color;
      }
    }

    li.welcome {
      width: 100%;
      background: transparent;
      padding: 0;
      max-width: 100%;
      justify-content: center;
      display: flex;
      .welcome-container {
        width: fit-content;
        border-radius: 24px;
        height: 152px;
        color: $chat-thread-text-color;
        background: $chat-thread-bgd-color;
        display: flex;
        justify-content: center;
        align-items: center;
        left: 50%;
        padding: 16px;

        .welcome-wrapper {
          display: flex;
          justify-content: space-between;
          gap: 16px;
        }

        .welcome-img {
          img {
            width: 120px;
            height: 120px;
            border-radius: 20px;
            background: #dee4ff;
          }
        }

        .welcome-text-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 5px;
          padding-right: 50px;
        }
      }
    }

    li.self {
      // animation: show-chat-odd 0.15s 1 ease-in;
      // -moz-animation: show-chat-odd 0.15s 1 ease-in;
      // -webkit-animation: show-chat-odd 0.15s 1 ease-in;
      float: right;
      color: $chat-thread-text-color;
      border-top-right-radius: 20px;
      border-top-left-radius: 20px;
      border-bottom-left-radius: 20px;
      background: #ced7ff;

      &:after {
        border-right: $chat-thread-msg-arrow-size solid transparent;
        border-bottom: $chat-thread-msg-arrow-size solid #ced7ff;
        right: -$chat-thread-msg-arrow-size;
      }
    }

    li.other {
      // animation: show-chat-even 0.15s 1 ease-in;
      // -moz-animation: show-chat-even 0.15s 1 ease-in;
      // -webkit-animation: show-chat-even 0.15s 1 ease-in;
      float: left;
      color: $chat-thread-text-color;
      border-top-right-radius: 20px;
      border-top-left-radius: 20px;
      border-bottom-right-radius: 20px;
      &:after {
        border-left: $chat-thread-msg-arrow-size solid transparent;
        left: -$chat-thread-msg-arrow-size;
      }
    }

    li.other.floating {
      position: fixed;
      z-index: 1;
      width: calc(100% - 63px);
      max-width: 425px;
      justify-content: center;
      align-items: center;
      display: flex;
      margin-top: -5px;
      background: radial-gradient(
          ellipse 100% 25.45% at 50% -0%,
          rgba(255, 251.7, 156.11, 0.6) 0%,
          rgba(255, 255, 255, 0) 100%
        ),
        radial-gradient(
          ellipse 100% 25.54% at 17.87% 100%,
          rgba(241.54, 255, 120.44, 0.4) 0%,
          rgba(255, 255, 255, 0) 60%
        ),
        linear-gradient(187deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0) 100%),
        linear-gradient(
          90deg,
          rgba(144.84, 19.81, 191.72, 0.9) 0%,
          rgba(206.34, 88.88, 255, 0.9) 25%,
          rgba(91.61, 89.89, 255, 0.9) 55%,
          rgba(71.47, 163.24, 255, 0.9) 85%
        ),
        white;
      padding: 8px 16px 8px 24px;
      backdrop-filter: blur(34.8px);
      box-shadow: 17.4px 17.4px 2.9px -16.4px rgba(255, 255, 255, 0.2) inset,
        -17.4px -17.4px 2.9px -17.4px rgba(255, 255, 255, 0.5) inset,
        4.8px 3.8px 5.8px -3px rgba(255, 255, 255, 1) inset, -5.8px -5.8px 5.8px -2.9px rgba(255, 255, 255, 0.75) inset,
        0 0 5.8px 5.8px rgba(255, 255, 255, 0.2) inset, 0 0 11.6px 0 rgba(0, 0, 0, 0.2);
      gap: 16px;

      div {
        color: #fff;
        font-weight: bold;
      }

      button {
        width: 70px;
        height: 36px;
        flex-shrink: 0;
        border-radius: 18px;
        border: 2px solid transparent;
        background: linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8) 0) padding-box,
          linear-gradient(to bottom, rgba(91, 122, 255, 1), rgba(91, 122, 255, 0)) border-box;
        color: #5b7aff;
        text-transform: unset;
        font-weight: bold;
      }
    }

    li.other.invisible {
      padding: 0;
      opacity: 0;
      margin: 0;
      height: 1px;
    }

    li.rate {
      animation: show-chat-even 0.15s 1 ease-in;
      -moz-animation: show-chat-even 0.15s 1 ease-in;
      -webkit-animation: show-chat-even 0.15s 1 ease-in;
      float: left;
      color: $chat-thread-text-color;
      border-radius: 20px;
      margin-top: -10px;
      padding: 8px 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;

      .thumb-up,
      .thumb-down {
        cursor: pointer;
      }

      .thumb-up {
        &.filled {
          :deep(path) {
            stroke: #5b7aff;
            fill: #9dafff;
            transition: all 0.2s ease;
          }
        }
      }

      .thumb-down {
        transform: rotate(180deg);
        &.filled {
          :deep(path) {
            stroke: #fe6767;
            fill: #fe676799;
            transition: all 0.2s ease;
          }
        }
      }
    }

    li.sending {
      padding: 15px 25px;

      .loader {
        width: 6px;
        aspect-ratio: 1;
        border-radius: 50%;
        animation: sending-loader 1s infinite linear alternate;
      }
    }
  }

  .footer {
    position: relative;
    flex-shrink: 0;
    display: flex;
    padding-top: 10px;
    min-height: 80px;
    max-height: 200px;
    background: transparent;
    margin-top: 5px;

    background: $chat-thread-bgd-color;
    width: 100%;
    color: $chat-thread-text-color;
    padding: 16px 48px 16px 16px;
    border-radius: 16px;

    .text-box {
      content: attr(data-placeholder);
      min-height: 100%;
      width: 100%;
      overflow-y: auto;

      @include fancy-scrollbar;

      &:empty::before {
        content: attr(data-placeholder);
        color: grey;
        display: block;
        pointer-events: none;
      }
    }

    button#sendMessage {
      position: absolute;
      top: 16px;
      right: 16px;
      padding: 0;

      :deep(.send-active path) {
        fill: #5b7aff;
        stroke: #5b7aff;
      }

      :deep(.send-disabled path) {
        fill: #78839c;
        stroke: #78839c;
      }
    }

    &.dashboard-chat-footer {
      margin: 8px 16px 16px 16px;
      border: 1px solid #d7dae1;
      width: initial;
    }
  }

  @mixin dashboard-colors {
    color: var(--ai-dahsboard-chat-text-color);
    background-color: var(--ai-dashboard-chat-background-color);
  }

  &.dashboard {
    @include dashboard-colors;
    .header {
      @include dashboard-colors;
    }
    .messages {
      scrollbar-color: var(--scrollbar-thumb);
      &::-webkit-scrollbar {
        width: $scrollbar-width;
      }
      &::-webkit-scrollbar-track {
        border-radius: $scrollbar-width;
        background-color: var(--scrollbar-track);
      }
      &::-webkit-scrollbar-thumb {
        border-radius: $scrollbar-width;
        background-color: var(--scrollbar-thumb);
      }
      li.other {
        background-color: $chat-thread-bgd-color-dashboard;

        &::after {
          border-bottom: 10px solid $chat-thread-bgd-color-dashboard;
        }
      }
      .welcome {
        .welcome-container {
          @include dashboard-colors;
        }
      }
    }
    .footer {
      @include dashboard-colors;
    }
  }
}

@keyframes sending-loader {
  0% {
    box-shadow: 10px 0 #d7dae1, -10px 0 #78839c;
    background: #d7dae1;
  }
  50% {
    box-shadow: 10px 0 #d7dae1, -10px 0 #d7dae1;
    background: #78839c;
  }
  100% {
    box-shadow: 10px 0 #78839c, -10px 0 #d7dae1;
    background: #d7dae1;
  }
}

@keyframes show-chat-even {
  0% {
    margin-left: -480px;
  }
  100% {
    margin-left: 0;
  }
}

@-moz-keyframes show-chat-even {
  0% {
    margin-left: -480px;
  }
  100% {
    margin-left: 0;
  }
}

@-webkit-keyframes show-chat-even {
  0% {
    margin-left: -480px;
  }
  100% {
    margin-left: 0;
  }
}

@keyframes show-chat-odd {
  0% {
    margin-right: -480px;
  }
  100% {
    margin-right: 0;
  }
}

@-moz-keyframes show-chat-odd {
  0% {
    margin-right: -480px;
  }
  100% {
    margin-right: 0;
  }
}

@-webkit-keyframes show-chat-odd {
  0% {
    margin-right: -480px;
  }
  100% {
    margin-right: 0;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  transition: all 0.5s ease;
  opacity: 0;
}
</style>
